import type { ButtonHTMLAttributes, ReactNode } from 'react';

import focus from './focus.module.css';
import styles from './Pill.module.css';

export interface PillProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'onSelect'> {
  readonly children: ReactNode;
  /** Present makes the pill a real toggle button, announced as pressed or not. */
  readonly selected?: boolean;
  readonly onSelect?: () => void;
}

/**
 * A rounded label, and - when it can be chosen - a toggle button carrying `aria-pressed`. A selected pill
 * that only looks selected is invisible to a screen reader, which is why the state is on the element
 * rather than in the class name alone.
 */
export function Pill({ children, selected, onSelect, className, ...rest }: PillProps): React.JSX.Element {
  const classes = [
    styles.pill,
    onSelect ? styles.selectable : '',
    onSelect ? focus.focusable : '',
    selected ? styles.selected : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  if (!onSelect) {
    return <span className={classes}>{children}</span>;
  }

  return (
    <button type="button" className={classes} aria-pressed={selected ?? false} onClick={onSelect} {...rest}>
      {children}
    </button>
  );
}
